/**
 * MetricHeroStats — shared grid of stat tiles used on the
 * PageHero across the app (PE Diagnostic, PE Goals, Intervention
 * Backlog, ImportPage, DataManagementHub — and any future
 * full-width hero that needs quick-glance numbers).
 *
 * Each page passes an array of tiles; this primitive handles the
 * layout (2-col mobile / 5-col desktop), per-tile styling, the
 * optional headline-span on lg, optional click-to-drill interaction,
 * and the "amber lowest-component pulse ring" pattern from the
 * PE Diagnostic hero.
 *
 * A tile value or hint can be a string *or* a React node, which lets
 * callers render compound values like "76.0% / 80%" without having
 * to fork the primitive.
 *
 * Light-mode styling matches the rest of the app's Option A
 * treatment: white tiles with a soft slate ring, slate-900 values,
 * slate-500 labels, slate-600 hint defaults, status hint tones
 * remapped to readable -700 shades.
 */
import React from 'react';
import { cn } from '../../lib/utils';

// Tone presets for the subtitle/hint row so callers can express
// above/below/neutral/CTA without re-stating Tailwind classes.
const HINT_TONE_CLASS = {
  default: 'text-slate-500',
  cta: 'text-amber-700 font-semibold',
  above: 'text-emerald-700 font-semibold',
  below: 'text-rose-700 font-semibold',
};

function HeroTile({ tile }) {
  const Wrapper = tile.onClick ? 'button' : 'div';
  const toneClass = HINT_TONE_CLASS[tile.hintTone] || HINT_TONE_CLASS.default;

  return (
    <Wrapper
      type={tile.onClick ? 'button' : undefined}
      onClick={tile.onClick || undefined}
      data-testid={tile.testId}
      data-metric-tile-headline={tile.headline ? 'true' : 'false'}
      title={tile.onClickTitle || undefined}
      className={cn(
        'group text-left rounded-xl bg-white px-4 py-3 border border-slate-200 ring-1 ring-slate-100 shadow-sm',
        'transition-all',
        tile.headline && 'col-span-2 lg:col-span-1',
        tile.onClick && 'hover:bg-slate-50 hover:ring-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#44b6d2]/30',
        tile.pulse && 'ring-2 ring-amber-300 ring-offset-2 ring-offset-white animate-pulse-slow border-amber-200',
      )}
    >
      <p className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        <span className="truncate">{tile.label}</span>
        {tile.badge}
      </p>
      <p className="text-3xl font-extrabold text-slate-900 tabular-nums leading-tight mt-0.5">
        {tile.value ?? '—'}
      </p>
      {tile.hint != null && (
        <p className={cn('text-[10.5px] mt-0.5', toneClass)}>{tile.hint}</p>
      )}
    </Wrapper>
  );
}

export function MetricHeroStats({ tiles, testId = 'metric-hero-stats', className }) {
  if (!tiles || tiles.length === 0) return null;
  return (
    <div
      className={cn('grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6', className)}
      data-testid={testId}
    >
      {tiles.map((t) => (
        <HeroTile key={t.key || t.label} tile={t} />
      ))}
    </div>
  );
}

export default MetricHeroStats;
